import type { LedgerEntry } from "../../api/types";
import { displayName, formatDateTime } from "../../lib/format";

export function HistoryFeed({
  entries,
  meUserID,
  loading
}: {
  entries: LedgerEntry[];
  meUserID: string;
  loading: boolean;
}) {
  const given = entries.filter(
    (entry) => entry.entry_type === "gumball_spend" && entry.source_user_id === meUserID
  );
  const received = entries.filter(
    (entry) => entry.entry_type === "gumball_spend" && entry.target_user_id === meUserID
  );

  return (
    <section className="card history">
      <header className="history__header">
        <h2 className="history__title">Season activity</h2>
        <p className="history__sub">Your gumballs given and received this season.</p>
      </header>
      <div className="history__columns">
        <FeedColumn
          heading="Given"
          rows={given}
          empty={loading ? "Loading…" : "Nothing yet — try the Give gumballs button above."}
          side="given"
        />
        <FeedColumn
          heading="Received"
          rows={received}
          empty={loading ? "Loading…" : "No gumballs received yet."}
          side="received"
        />
      </div>
    </section>
  );
}

function FeedColumn({
  heading,
  rows,
  empty,
  side
}: {
  heading: string;
  rows: LedgerEntry[];
  empty: string;
  side: "given" | "received";
}) {
  return (
    <div className="history__column">
      <h3 className="history__column-title">{heading}</h3>
      {rows.length === 0 ? (
        <p className="history__empty">{empty}</p>
      ) : (
        <ul className="history__list">
          {rows.map((row) => (
            <li key={row.id} className="history__row">
              <div className="history__row-text">
                <strong>{counterpartLabel(row, side)}</strong>
                {row.reason !== null && row.reason.trim() !== "" && (
                  <span className="history__row-note">{row.reason}</span>
                )}
                <time>{formatDateTime(row.created_at)}</time>
              </div>
              <span className="history__row-amount">{row.amount}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// counterpartLabel renders the "other party" for a feed row. Given side
// shows the recipient (always visible to the giver). Received side shows
// the sender — when masking has nulled source_user_id, render "Anonymous".
function counterpartLabel(row: LedgerEntry, side: "given" | "received"): string {
  if (side === "given") {
    return displayName(null, row.target_user_email);
  }
  if (row.source_user_id === null) {
    return "Anonymous";
  }
  return displayName(null, row.source_user_email);
}
